import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChartBarIcon, ShieldCheckIcon, UserGroupIcon, GlobeAltIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const AboutUsPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'About Us' }
  ];

  const values = [
    {
      icon: ChartBarIcon,
      title: "Expert Investment",
      description: "Our team of experts brings together years of experience in traditional finance, blockchain technology, and investment management."
    },
    {
      icon: ShieldCheckIcon,
      title: "Transparency & Security",
      description: "We prioritize transparency in all our operations, providing clients with unparalleled insight into their investments."
    },
    {
      icon: UserGroupIcon,
      title: "Client-Centered Approach",
      description: "We take the time to understand your ambitions and create unique solutions for every stage of your life's journey."
    },
    {
      icon: GlobeAltIcon,
      title: "Global Presence",
      description: "With presence in Spain, Portugal, Luxembourg, Turkey, Mexico, Peru, Colombia and Argentina, we offer truly international investment solutions."
    }
  ];

  const stats = [
    { value: "15+", label: "Years of Excellence" },
    { value: "$5B+", label: "Assets Managed" },
    { value: "700+", label: "Team Members" },
    { value: "150+", label: "Investment Professionals" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-50 to-white pt-20 lg:pt-24 pb-16 lg:pb-24 overflow-hidden">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="space-y-8"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="space-y-6"
              >
                <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 font-heading leading-tight">
                  We are
                  <span className="block text-teal-600">Segway Capital</span>
                </h1>
                <p className="text-xl lg:text-2xl text-gray-600 leading-relaxed">
                  Pioneering the future of investment management through cutting-edge blockchain technology and unparalleled client service.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="grid grid-cols-2 gap-6"
              >
                {stats.slice(0, 2).map((stat, index) => (
                  <div key={index} className="text-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
                    <div className="text-3xl lg:text-4xl font-bold text-teal-600 mb-2">{stat.value}</div>
                    <div className="text-gray-600 text-sm lg:text-base">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="/assets/business-team-working-together.jpg" 
                  alt="Segway Capital Team" 
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Mission Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-16 lg:mb-20"
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 font-heading">
              Our Mission
            </h2>
            <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              At Segway Capital, we're dedicated to revolutionizing investment management by combining traditional financial expertise with cutting-edge technology.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.8, 
                    delay: index * 0.1,
                    ease: [0.25, 0.46, 0.45, 0.94] 
                  }}
                  viewport={{ once: true }}
                  className="group bg-gray-50 rounded-3xl p-8 hover:bg-white hover:shadow-xl transition-all duration-500 border border-gray-100"
                >
                  <div className="mb-6">
                    <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center group-hover:bg-teal-600 transition-colors duration-500">
                      <IconComponent className="w-8 h-8 text-teal-600 group-hover:text-white transition-colors duration-500" />
                    </div>
                  </div>
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4 font-heading">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="/assets/co-workers-discussing-business-project.jpg" 
                  alt="Our Approach" 
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 font-heading leading-tight">
                  Our Approach
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed">
                  We work with a team of highly experienced individuals to deliver superior asset management solutions to a diverse range of clients.
                </p>
              </div>

              <div className="space-y-6">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                  className="group bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300"
                >
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4 font-heading">
                    Wealth Management Solutions
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Segway Capital Management offers a personal and proactive approach to managing your wealth. We create unique solutions for every stage of your life's journey.
                  </p>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="group bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300"
                >
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4 font-heading">
                    Investment Advisory
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Our Investment Advisory services provide expert guidance and personalized recommendations based on your financial goals and risk tolerance.
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Global Presence Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-16 lg:mb-20"
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 font-heading">
              Our Global Presence
            </h2>
            <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              With a local presence in multiple countries, we provide specialized services worldwide while understanding local market dynamics.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-3xl p-8 border border-gray-100"
            >
              <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 font-heading">Europe</h3>
              <div className="space-y-4">
                {['Spain', 'Portugal', 'Luxembourg'].map((country, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-teal-600 rounded-full"></div>
                    <span className="text-lg text-gray-600">{country}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-3xl p-8 border border-gray-100"
            >
              <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 font-heading">Latin America</h3>
              <div className="space-y-4">
                {['Mexico', 'Peru', 'Colombia', 'Argentina'].map((country, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-teal-600 rounded-full"></div>
                    <span className="text-lg text-gray-600">{country}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-3xl p-8 border border-gray-100"
            >
              <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 font-heading">Middle East</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-teal-600 rounded-full"></div>
                  <span className="text-lg text-gray-600">Turkey</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6 font-heading">
              Ready to Start Your Investment Journey?
            </h2>
            <p className="text-xl lg:text-2xl text-teal-100 mb-8 leading-relaxed">
              Join thousands of satisfied clients who trust Segway Capital with their financial future.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-teal-600 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-gray-50 transition-colors inline-flex items-center gap-3"
              >
                Get Started Today
                <ArrowRightIcon className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white hover:bg-white hover:text-teal-600 px-8 py-4 rounded-2xl font-bold text-lg transition-colors"
              >
                Learn More
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutUsPage; 